/**
 * Recepción — vista principal.
 *
 * Estados:
 *   - tab=pendientes / list  → cards de transacciones ANMAT pendientes.
 *   - click en una card     → detalle (RecepcionDetalleView) — pega a riga
 *                              filtrando por remito (con fallback a factura).
 *   - tab=completadas       → recepciones confirmadas (mock todavía).
 */

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useSessionStore } from '@/stores/session';
import { PendientesRecepcionList } from './PendientesRecepcionList';
import { CompletadasRecepcionTable } from './CompletadasRecepcionTable';
import { RecepcionDetalleView } from './RecepcionDetalleView';
import type { RecepcionRemito } from '@/api/contracts/recepcion-pendientes';

type Tab = 'pendientes' | 'completadas';

export function RecepcionPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('pendientes');
  const [remitoActivo, setRemitoActivo] = useState<RecepcionRemito | null>(null);

  // Cambio de GLN → volvemos al listado, mismo patrón que despacho.
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);
  const ubicacionPrevia = useRef(ubicacionActivaId);
  useEffect(() => {
    if (ubicacionPrevia.current !== ubicacionActivaId) {
      ubicacionPrevia.current = ubicacionActivaId;
      setRemitoActivo(null);
    }
  }, [ubicacionActivaId]);

  // Al volver al listado, invalidamos para que el conteo de pendientes
  // refleje cualquier cambio que haya ocurrido.
  const remitoPrevio = useRef(remitoActivo);
  useEffect(() => {
    if (remitoPrevio.current && !remitoActivo) {
      void qc.invalidateQueries({ queryKey: ['recepcion-pendientes'] });
    }
    remitoPrevio.current = remitoActivo;
  }, [remitoActivo, qc]);

  if (remitoActivo) {
    return (
      <RecepcionDetalleView remito={remitoActivo} onVolver={() => setRemitoActivo(null)} />
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-everex-azul">Recepción</CardTitle>
          <p className="text-xs text-gray-600">
            Transacciones ANMAT entrantes (otros eslabones declararon egreso hacia este GLN).
            Ingresá un número de remito para buscarlo.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 border-b border-gray-200">
            <TabButton active={tab === 'pendientes'} onClick={() => setTab('pendientes')}>
              Pendientes
            </TabButton>
            <TabButton active={tab === 'completadas'} onClick={() => setTab('completadas')}>
              Completadas
            </TabButton>
          </div>
        </CardContent>
      </Card>

      {tab === 'pendientes' ? (
        <PendientesRecepcionList onAbrirRemito={setRemitoActivo} />
      ) : (
        <CompletadasRecepcionTable onAbrirRemito={setRemitoActivo} />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-4 py-2 text-sm transition',
        active
          ? 'border-b-2 border-everex-naranja font-semibold text-everex-azul'
          : 'text-gray-600 hover:text-everex-azul',
      )}
    >
      {children}
    </button>
  );
}
